import { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Check } from 'lucide-react';
import { api } from '../../services/api';
import { fmtNumber, fmtDate, categoryColor, statusColor } from './format';
import { c, t, tbl } from '../../theme';
import { useSort, sortRows, SortTh } from '../Shared/sortable';
import ExpandableScroll from '../Shared/ExpandableScroll';
import { useRowLimit, ShowMoreRow } from '../Shared/rowLimit';
import NotesCell from '../Shared/NotesCell';

// Matches the real "Vacancies" tab column set, PLUS the columns Recruiting
// was still checking the native Workday "Job Requisitions - Linda" report
// for directly (In Offer, # in Offer Stage, Candidate Name 1/2 in Offer,
// Background Check Event/Initiated, Candidate in Background Check, New
// Position, Replacement for Worker, Justification, Ready for Hire) -- added
// Nov 2026 once Jesse flagged that the whole point of this view is to
// replace that report, not just mirror the older Excel tracker that
// happened to hide them too. All of these were already parsed, stored and
// selected by the API; this table just never rendered them.
//
// Sourced Candidates, Prescreens Conducted, Interviews Scheduled, Notes and
// Offer Signed are Recruiting-owned (see backend/src/routes/staffing.js
// PATCH /vacancies/:requisition_id/notes) — editable inline here when
// canEdit is true. Recruiting fills the first four in by hand (most likely
// so the Workday export can re-import cleanly into their own Excel process),
// which only became a real risk once Vacancies started refreshing daily from
// an automated feed and wiping/replacing the row wholesale every morning.
//
// Offer Signed replaces the old manual convention of overwriting the Cost
// Center cell with the text "Offer" in the source spreadsheet once a
// candidate signs: the row keeps its real cost center and still shows here
// (as a pill, click to toggle), it's just excluded from the vacancy-count KPI.
//
// Candidate names and background-check fields are person-level detail, same
// sensitivity tier as a New Hire's name — GET /vacancies strips them from
// the row entirely (not just blanks them) for a viewer without showPeople
// access at that cost center, so their presence/absence on the row object
// is how we detect whether to show those columns at all, same probe pattern
// NewHireTable.jsx uses for 'worker_name'.
export default function VacancyTable({ vacancies, canEdit = false }) {
  const showPeople = vacancies.some((v) => 'candidate_name_1_in_offer' in v);
  const sort = useSort();
  const sorted = sortRows(vacancies, sort.key, sort.dir, {
    requisition:  (v) => [v.requisition_id, v.requisition_title].filter(Boolean).join(' '),
    job_profile:  (v) => v.job_profile || '',
    start_date:   (v) => v.recruiting_start_date || '',
    location:     (v) => v.raw_location || '',
    supervisory:  (v) => v.supervisory_org || '',
    openings:     (v) => Number(v.openings_available || 0),
    cost_center:  (v) => v.cc_code ? `${v.cc_code} ${v.cc_name}` : '',
    pay_rate:     (v) => v.pay_rate_type || '',
    category:     (v) => v.recruiting_category || '',
    days_open:    (v) => Number(v.days_open || 0),
    applicants:   (v) => Number(v.active_candidates || 0),
    sourced:      (v) => Number(v.sourced_candidates || 0),
    prescreens:   (v) => Number(v.pre_screen_conducted || 0),
    interviews:   (v) => Number(v.interviews_scheduled || 0),
    in_offer:       (v) => v.in_offer ? 1 : 0,
    offer_stage:    (v) => Number(v.in_offer_stage_count || 0),
    candidate1:     (v) => v.candidate_name_1_in_offer || '',
    candidate2:     (v) => v.candidate_name_2_in_offer || '',
    bg_event:       (v) => v.background_check_event || '',
    bg_initiated:   (v) => v.background_check_initiated || '',
    bg_candidate:   (v) => v.candidate_in_background_check || '',
    new_position:   (v) => v.new_position ? 1 : 0,
    replacement:    (v) => v.replacement_for_worker || '',
    justification:  (v) => v.justification || '',
    ready_for_hire: (v) => Number(v.ready_for_hire_count || 0),
  });
  const colCount = 15 + 6 + (showPeople ? 5 : 0);
  const limiter = useRowLimit(sorted);
  return (
    <ExpandableScroll collapsedHeight={520}>
      <table style={s.table}>
        <thead>
          <tr>
            <SortTh label="Job Requisition" sortKey="requisition" sort={sort} sticky style={{ ...tbl.th, padding: '9px 14px 9px 11px' }} />
            <SortTh label="Job Profile" sortKey="job_profile" sort={sort} sticky />
            <SortTh label="Recruiting Start Date" sortKey="start_date" sort={sort} sticky />
            <SortTh label="Primary Location" sortKey="location" sort={sort} sticky />
            <SortTh label="Supervisory Organization" sortKey="supervisory" sort={sort} sticky />
            <SortTh label="Openings Available" sortKey="openings" sort={sort} align="right" sticky />
            <SortTh label="Cost Center" sortKey="cost_center" sort={sort} sticky />
            <SortTh label="Pay Rate Type" sortKey="pay_rate" sort={sort} sticky />
            <SortTh label="Category" sortKey="category" sort={sort} sticky />
            <SortTh label="Days Open" sortKey="days_open" sort={sort} align="right" sticky />
            <SortTh label="Applicants" sortKey="applicants" sort={sort} align="right" sticky />
            <SortTh label="Sourced Candidates" sortKey="sourced" sort={sort} align="right" sticky />
            <SortTh label="Prescreens Conducted" sortKey="prescreens" sort={sort} align="right" sticky />
            <SortTh label="Interviews Scheduled" sortKey="interviews" sort={sort} align="right" sticky />
            <SortTh label="Notes" sticky />
            <SortTh label="In Offer" sortKey="in_offer" sort={sort} align="center" sticky />
            <SortTh label="# in Offer Stage" sortKey="offer_stage" sort={sort} align="right" sticky />
            {showPeople && <SortTh label="Candidate Name 1 in Offer" sortKey="candidate1" sort={sort} sticky />}
            {showPeople && <SortTh label="Candidate Name 2 in Offer" sortKey="candidate2" sort={sort} sticky />}
            {showPeople && <SortTh label="Background Check Event" sortKey="bg_event" sort={sort} sticky />}
            {showPeople && <SortTh label="Background Check Initiated" sortKey="bg_initiated" sort={sort} sticky />}
            {showPeople && <SortTh label="Candidate in Background Check" sortKey="bg_candidate" sort={sort} sticky />}
            <SortTh label="New Position" sortKey="new_position" sort={sort} align="center" sticky />
            <SortTh label="Replacement for Worker" sortKey="replacement" sort={sort} sticky />
            <SortTh label="Justification" sortKey="justification" sort={sort} sticky />
            <SortTh label="Ready for Hire" sortKey="ready_for_hire" sort={sort} align="right" sticky />
          </tr>
        </thead>
        <tbody>
          {limiter.visible.map((v, i) => (
            <VacancyRow key={v.id} vacancy={v} canEdit={canEdit} showPeople={showPeople} index={i} />
          ))}
          <ShowMoreRow limiter={limiter} colSpan={colCount} />
          {!vacancies.length && (
            <tr><td colSpan={colCount} style={s.empty}>No open vacancies here.</td></tr>
          )}
        </tbody>
      </table>
    </ExpandableScroll>
  );
}

function VacancyRow({ vacancy: v, canEdit, showPeople, index }) {
  const qc = useQueryClient();
  const [sourcedCandidates, setSourcedCandidates] = useState(v.sourced_candidates ?? '');
  const [preScreenConducted, setPreScreenConducted] = useState(v.pre_screen_conducted ?? '');
  const [interviewsScheduled, setInterviewsScheduled] = useState(v.interviews_scheduled ?? '');
  const [notes, setNotes] = useState(v.notes ?? '');

  // Re-sync whenever the underlying row's own values change — not just on
  // mount. staffing_roster/staffing_vacancies rows keep the same `id` across
  // the "all cost centers" list and a focused cost center's own list (both
  // read the same current batch), so switching between those views reuses
  // this row's component instance rather than remounting it; without this,
  // the edit boxes kept showing whatever the FIRST view's values were.
  useEffect(() => {
    setSourcedCandidates(v.sourced_candidates ?? '');
    setPreScreenConducted(v.pre_screen_conducted ?? '');
    setInterviewsScheduled(v.interviews_scheduled ?? '');
    setNotes(v.notes ?? '');
  }, [v.sourced_candidates, v.pre_screen_conducted, v.interviews_scheduled, v.notes]);

  const saveMutation = useMutation({
    mutationFn: (payload) => api.patch(`/staffing/vacancies/${v.requisition_id}/notes`, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['staffing-vacancies'] });
      qc.invalidateQueries({ queryKey: ['staffing-costcenter'] });
    },
  });

  // Each save upserts the whole Recruiting-owned row, so every field not being
  // changed right now has to be re-sent as-is, same reason saveInterviews()
  // already carried notes along before this -- otherwise whichever field the
  // other input last saved gets silently cleared.
  const basePayload = () => ({
    sourced_candidates:   v.sourced_candidates ?? null,
    pre_screen_conducted: v.pre_screen_conducted ?? null,
    interviews_scheduled: v.interviews_scheduled ?? null,
    notes:                v.notes ?? null,
  });

  function saveSourcedCandidates() {
    const num = sourcedCandidates === '' ? null : Number(sourcedCandidates);
    if (num === (v.sourced_candidates ?? null)) return;
    saveMutation.mutate({ ...basePayload(), sourced_candidates: num });
  }
  function savePreScreenConducted() {
    const num = preScreenConducted === '' ? null : Number(preScreenConducted);
    if (num === (v.pre_screen_conducted ?? null)) return;
    saveMutation.mutate({ ...basePayload(), pre_screen_conducted: num });
  }
  function saveInterviews() {
    const num = interviewsScheduled === '' ? null : Number(interviewsScheduled);
    if (num === v.interviews_scheduled) return;
    saveMutation.mutate({ ...basePayload(), interviews_scheduled: num });
  }
  function saveNotes() {
    if (notes === (v.notes ?? '')) return;
    saveMutation.mutate({ ...basePayload(), notes: notes || null });
  }
  function toggleOfferSigned() {
    saveMutation.mutate({ ...basePayload(), offer_signed: !v.offer_signed });
  }

  const jobRequisition = [v.requisition_id, v.requisition_title].filter(Boolean).join(' ');
  const costCenter = v.cc_code ? `${v.cc_code} — ${v.cc_name}` : '—';
  const canEditThisRow = canEdit && !!v.requisition_id;
  const statusC = statusColor(v.requisition_status);
  const catC = categoryColor(v.recruiting_category);
  const daysOpenColor = v.days_open >= 60 ? c.dangerText : v.days_open >= 30 ? c.warningText : c.textStrong;

  return (
    <tr style={{ ...tbl.trZebra(index), ...(v.is_filled ? { opacity: 0.6 } : null) }}>
      <td style={{ ...s.tdDesc, padding: '9px 14px 9px 11px', borderLeft: `3px solid ${catC.border}` }} title={jobRequisition}>
        <div style={s.titleCell}>
          <span>{jobRequisition || '—'}</span>
          {v.requisition_status && (
            <span style={{ ...s.badge, background: statusC.bg, color: statusC.color }}>{v.requisition_status}</span>
          )}
          {canEditThisRow ? (
            <button
              type="button"
              onClick={toggleOfferSigned}
              title={v.offer_signed ? 'Click to unmark' : 'Mark offer signed'}
              style={{ ...(v.offer_signed ? s.offerPillOn : s.offerPillOff), cursor: 'pointer' }}
            >
              <CheckCircle2 size={11} />
              Offer signed
            </button>
          ) : v.offer_signed && (
            <span style={s.offerPillOn} title={v.offer_signed_at ? `Signed ${fmtDate(v.offer_signed_at)}` : undefined}>
              <CheckCircle2 size={11} />
              Offer signed
            </span>
          )}
        </div>
      </td>
      <td style={s.tdDesc} title={v.job_profile}>{v.job_profile || '—'}</td>
      <td style={tbl.td}>{fmtDate(v.recruiting_start_date)}</td>
      <td style={tbl.td}>{v.raw_location || '—'}</td>
      <td style={tbl.td}>{v.supervisory_org || '—'}</td>
      <td style={tbl.tdR}>{fmtNumber(v.openings_available)}</td>
      <td style={tbl.td}>{costCenter}</td>
      <td style={tbl.td}>{v.pay_rate_type || '—'}</td>
      <td style={tbl.td}>
        {v.recruiting_category
          ? <span style={{ ...s.badge, background: catC.bg, color: catC.color }}>{v.recruiting_category}</span>
          : '—'}
      </td>
      <td style={{ ...tbl.tdR, color: daysOpenColor, fontWeight: v.days_open >= 30 ? 600 : 400 }}>{fmtNumber(v.days_open)}</td>
      <td style={tbl.tdR}>{fmtNumber(v.active_candidates)}</td>
      <td style={tbl.tdR}>
        {canEditThisRow ? (
          <input
            type="number" min="0" style={s.editInput}
            value={sourcedCandidates}
            onChange={(e) => setSourcedCandidates(e.target.value)}
            onBlur={saveSourcedCandidates}
          />
        ) : (fmtNumber(v.sourced_candidates))}
      </td>
      <td style={tbl.tdR}>
        {canEditThisRow ? (
          <input
            type="number" min="0" style={s.editInput}
            value={preScreenConducted}
            onChange={(e) => setPreScreenConducted(e.target.value)}
            onBlur={savePreScreenConducted}
          />
        ) : (fmtNumber(v.pre_screen_conducted))}
      </td>
      <td style={tbl.tdR}>
        {canEditThisRow ? (
          <input
            type="number" min="0" style={s.editInput}
            value={interviewsScheduled}
            onChange={(e) => setInterviewsScheduled(e.target.value)}
            onBlur={saveInterviews}
          />
        ) : (fmtNumber(v.interviews_scheduled))}
      </td>
      <td style={s.tdNotes}>
        <NotesCell value={notes} onChange={setNotes} onBlur={saveNotes} canEdit={canEditThisRow} />
      </td>
      <td style={s.tdCenter}>{v.in_offer ? <Check size={14} color={c.success} /> : <span style={s.dash}>—</span>}</td>
      <td style={tbl.tdR}>{fmtNumber(v.in_offer_stage_count)}</td>
      {showPeople && <td style={tbl.td}>{v.candidate_name_1_in_offer || '—'}</td>}
      {showPeople && <td style={tbl.td}>{v.candidate_name_2_in_offer || '—'}</td>}
      {showPeople && <td style={tbl.td}>{v.background_check_event || '—'}</td>}
      {showPeople && <td style={tbl.td}>{v.background_check_initiated || '—'}</td>}
      {showPeople && <td style={tbl.td}>{v.candidate_in_background_check || '—'}</td>}
      <td style={s.tdCenter}>{v.new_position ? <Check size={14} color={c.success} /> : <span style={s.dash}>—</span>}</td>
      <td style={s.tdDesc} title={v.replacement_for_worker}>{v.replacement_for_worker || '—'}</td>
      <td style={s.tdDesc} title={v.justification}>{v.justification || '—'}</td>
      <td style={tbl.tdR}>{fmtNumber(v.ready_for_hire_count)}</td>
    </tr>
  );
}

const s = {
  table:       { width: '100%', borderCollapse: 'collapse', fontSize: t.bodySm.fontSize },
  empty:       { textAlign: 'center', padding: '24px', color: c.textFaint },
  tdDesc:      { ...tbl.td, maxWidth: '240px' },
  titleCell:   { display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' },
  tdNotes:     { ...tbl.td, minWidth: '200px' },
  tdCenter:    { ...tbl.td, textAlign: 'center' },
  dash:        { color: c.borderStrong },
  editInput:   { width: '60px', padding: '4px 6px', border: `1px solid ${c.borderStrong}`, borderRadius: '4px', fontSize: t.caption.fontSize, fontFamily: 'inherit', textAlign: 'right' },
  badge:       { display: 'inline-block', padding: '2px 8px', borderRadius: '10px', fontSize: t.micro.fontSize, fontWeight: 600, whiteSpace: 'nowrap' },
  offerPillOn: {
    display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '2px 8px', borderRadius: '10px',
    fontSize: t.micro.fontSize, fontWeight: 600, whiteSpace: 'nowrap', background: c.successTint, color: c.successText,
    border: '1px solid transparent', fontFamily: 'inherit', cursor: 'default',
  },
  offerPillOff: {
    display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '2px 8px', borderRadius: '10px',
    fontSize: t.micro.fontSize, fontWeight: 500, whiteSpace: 'nowrap', background: 'transparent', color: c.textFaint,
    border: `1px dashed ${c.borderStrong}`, fontFamily: 'inherit', cursor: 'pointer',
  },
};
